<script lang="ts">
  import GameSettings from '../panels/GameSettings.svelte';
  import Button from '../ui/Button.svelte';
  import Card from '../ui/Card.svelte';
  import Dialog from '../ui/Dialog.svelte';
  import DialogHeader from '../ui/DialogHeader.svelte';
  import LobbyControlBar from './LobbyControlBar.svelte';
  import LobbyDetailsTabs from './LobbyDetailsTabs.svelte';
  import ThemeIcon from '../themes/ThemeIcon.svelte';
  import { MODE_INFO } from '../content/modes';
  import type { GameMode } from '../../shared/modes';
  import type { GameConfig } from '../../shared/config';
  import type { ChatMessage, PlayerView } from '../../shared/protocol';

  interface Props {
    mode: GameMode;
    roomCode: string;
    players: readonly Readonly<PlayerView>[];
    meId: string;
    isHost: boolean;
    /** Only the role holder removes players; a stand-in host cannot. */
    canKick: boolean;
    config: GameConfig;
    settingsEditable: boolean;
    updateConfig: (patch: Partial<GameConfig>) => void;
    /** Full invite URL for the title tooltip; the button shows the room code. */
    inviteUrl: string;
    shareRoomLink: () => void;
    startRound: () => void;
    leaveRoom: () => void;
    resetScores: () => void;
    kickPlayer: (id: string) => void;
    chat: readonly Readonly<ChatMessage>[];
    sendChat: (text: string) => boolean;
    connected: boolean;
  }
  let {
    mode,
    roomCode,
    players,
    meId,
    isHost,
    canKick,
    config,
    settingsEditable,
    updateConfig,
    inviteUrl,
    shareRoomLink,
    startRound,
    leaveRoom,
    resetScores,
    kickPlayer,
    chat,
    sendChat,
    connected,
  }: Props = $props();

  let rulesOpen = $state(false);
  let rulesButton = $state<HTMLElement | null>(null);

  function openRules(): void {
    rulesOpen = true;
  }

  function closeRules(): void {
    rulesOpen = false;
  }
</script>

<section id="screen-lobby" class="screen">
  <div class="lobby-header">
    <h1>{mode === 'versus' ? 'Lobby' : MODE_INFO[mode].title}</h1>
    {#if mode === 'versus'}
      <div class="lobby-actions">
        <Button
          bind:ref={rulesButton}
          size="small"
          class="lobby-rules"
          title={settingsEditable ? 'Change the room rules' : 'View the room rules'}
          onclick={openRules}>Rules</Button
        >
        <Button
          size="small"
          class="lobby-share"
          aria-label="Share room invite"
          title={inviteUrl}
          onclick={shareRoomLink}><span class="code">{roomCode}</span><ThemeIcon name="share" /></Button
        >
      </div>
    {/if}
  </div>
  <LobbyControlBar {mode} {isHost} {players} {config} {startRound} {leaveRoom} />
  <LobbyDetailsTabs
    {config}
    {players}
    {meId}
    {isHost}
    {canKick}
    {kickPlayer}
    {resetScores}
    {chat}
    {sendChat}
    {connected}
  />
</section>

{#if mode === 'versus'}
  <Dialog
    open={rulesOpen}
    labelledby="room-rules-title"
    presentation="large"
    dismissOnBackdrop
    returnFocus={() => rulesButton}
    onDismiss={closeRules}
  >
    {#snippet header()}
      <DialogHeader
        id="room-rules-title"
        title="Room rules"
        level={1}
        closeLabel="Close Room Rules"
        close={closeRules}
      />
    {/snippet}
    {#if !settingsEditable}<p class="hint">Only the host can change these.</p>{/if}
    <Card><GameSettings {config} editable={settingsEditable} change={updateConfig} /></Card>
  </Dialog>
{/if}

<style>
  #screen-lobby {
    gap: 14px;
  }
  .lobby-header {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 12px;
    width: 100%;
  }
  /* Title yields first on narrow phones so Rules and Share never spill past the card edge. */
  .lobby-header h1 {
    flex-shrink: 0;
    font-size: clamp(30px, 10vw, 36px);
    letter-spacing: -1px;
  }
  .lobby-actions {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
    flex-shrink: 0;
  }
  .lobby-actions :global(.btn.lobby-rules) {
    flex-shrink: 0;
  }
  .lobby-actions :global(.btn.lobby-share) {
    max-width: 100%;
    flex-shrink: 0;
  }
</style>
